import {
  CloudUploadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  message,
  Progress,
  Row,
  Space,
  Statistic,
  Switch,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { UploadChangeParam } from 'antd/es/upload/interface';
import * as Component from 'components';
import { contentAssetService } from 'content/library/service/contentAssetService';
import { romanize } from 'koroman';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { contentApiService } from 'service/api/contentApiService';
import { videoService } from 'service/api/videoService';
import { uploadVideoStore } from 'src/domain/upload/upload-video/store/uploadVideoStore';
import { accountStore } from 'store/accountStore';
import { UploadVideoInterface } from 'type/uploadVideoType';

const { Text, Title } = Typography;
const { Dragger } = Upload;

// Slugify utility function (supports Korean) - converts Korean to romanized English
const slugify = (text: string): string => {
  // 한글을 로마자로 변환 (koroman 라이브러리 사용)
  const converted = romanize(text);

  return converted
    .toLowerCase()
    .replace(/[!@#$%^&*()_+={}\[\]|\\:";'<>?,.\/~`]/g, '') // Remove only common special characters
    .replace(/\./g, '') // Remove dots specifically
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

interface UploadStats {
  totalFiles: number;
  completedFiles: number;
  uploadingFiles: number;
  failedFiles: number;
  normalFiles: number;
  totalSize: number;
  uploadedSize: number;
  avgSpeed: number;
}

const AssetAdd: React.FC = () => {
  const [uploading, setUploading] = useState(false);
  const [createContent, setCreateContent] = useState(false);

  // Get file list from uploadVideoStore
  const fileList = uploadVideoStore.getUploadVideoList();

  // Calculate upload statistics
  const getUploadStats = (): UploadStats => {
    const totalFiles = fileList.length;
    const completedFiles = fileList.filter(file => file.state === 'uploaded').length;
    const uploadingFiles = fileList.filter(file => file.state === 'uploading').length;
    const failedFiles = fileList.filter(file => file.state === 'failed').length;
    const normalFiles = fileList.filter(file => file.state === 'normal').length;
    const totalSize = fileList.reduce((acc, file) => acc + (file.size || 0), 0);

    // Calculate uploaded size (based on progress)
    const uploadedSize = fileList.reduce((acc, file) => {
      const fileSize = file.size || 0;
      if (file.state === 'uploaded') {
        return acc + fileSize;
      } else if (file.state === 'uploading') {
        const progress = Math.max(0, Math.min(100, file.percent || 0));
        return acc + (fileSize * progress) / 100;
      }
      return acc;
    }, 0);

    // Calculate average upload speed (average of uploading files)
    const uploadingWithSpeed = fileList.filter(
      file => file.state === 'uploading' && (file.speed || 0) > 0
    );
    const avgSpeed =
      uploadingWithSpeed.length > 0
        ? uploadingWithSpeed.reduce((acc, file) => acc + (file.speed || 0), 0) /
          uploadingWithSpeed.length
        : 0;

    return {
      totalFiles,
      completedFiles,
      uploadingFiles,
      failedFiles,
      normalFiles,
      totalSize,
      uploadedSize,
      avgSpeed,
    };
  };

  // File change handler
  const handleFileChange = (info: UploadChangeParam<any>) => {
    uploadVideoStore.setUploadVideoList(info as UploadChangeParam<UploadVideoInterface>);
  };

  // Execute actual upload
  const handleUpload = async () => {
    const uploadableFiles = fileList.filter(
      file => file.state === 'normal' || file.state === 'failed'
    );

    if (uploadableFiles.length === 0) {
      if (fileList.length === 0) {
        message.warning('Please select files to upload.');
      } else {
        message.info('No new files to upload.');
      }
      return;
    }

    setUploading(true);
    try {
      // Upload videos first
      await videoService.uploadVideoFiles(uploadableFiles, createContent);
      // If createContent is enabled, check for successfully uploaded files and create content
      if (createContent) {
        // Check if any files were actually uploaded successfully
        const successfullyUploadedFiles = uploadableFiles.filter(file => file.state === 'uploaded');

        if (successfullyUploadedFiles.length > 0) {
          await handleCreateContentForUploads(successfullyUploadedFiles);
        } else {
          message.warning('No files were uploaded successfully. Cannot create content.');
        }
      }
      message.success(`${uploadableFiles.length} files uploaded successfully.`);
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Error occurred while uploading some files.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateContentForUploads = async (uploadedFiles: UploadVideoInterface[]) => {
    try {
      let createdCount = 0;
      let videoAssetCount = 0;
      const userPlatform = accountStore.getAccount()?.platform;

      for (const file of uploadedFiles) {
        if (file.state === 'uploaded') {
          const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
          const title = fileNameWithoutExt;
          const slug = slugify(fileNameWithoutExt);
          // Create content
          const contentData: any = {
            title,
            slug,
            status: 'draft',
            content_type: 'tv-series',
          };

          if (userPlatform?.id === 2) {
            contentData.skip_transcode = true;
          }
          const newContent = await contentApiService.create(contentData);
          createdCount++;
          // Get video ID from the uploaded file response
          let videoId = null;
          if (file.response && typeof file.response === 'object' && file.response.id) {
            videoId = file.response.id;
          }

          // Create video asset if video ID is available
          if (videoId && newContent.id) {
            try {
              await contentAssetService.saveContentVideoAssets(newContent.id, [
                {
                  content: newContent.id,
                  video: videoId,
                  subtitle_ids: null,
                  service_name: '',
                  is_default: true,
                  is_subtitles_embedded: false,
                  stream_manifests: null,
                  isNew: true,
                },
              ]);

              videoAssetCount++;
            } catch {
              message.warning(`Content "${title}" created but video asset could not be linked.`);
            }
          } else {
            message.warning(
              `Content "${title}" created but video could not be linked automatically.`
            );
          }
        }
      }

      if (createdCount > 0) {
        const assetMessage =
          videoAssetCount > 0
            ? ` ${videoAssetCount}/${createdCount} video assets linked automatically.`
            : ' Please manually link videos in the content edit pages.';

        message.success(`${createdCount} contents created successfully!${assetMessage}`);
      }
    } catch (error) {
      console.error('Error creating content:', error);
      message.error('Some contents could not be created.');
    }
  };
  // Remove file
  const handleRemoveFile = (file: any) => {
    const videoFile = file as UploadVideoInterface;
    if (videoFile.state === 'uploading') {
      message.warning('Cannot remove file while uploading.');
      return;
    }
    uploadVideoStore.removeUploadVideoList(videoFile.uid);
  };

  // Clear all files
  const handleClearAll = () => {
    const uploadingFiles = fileList.filter(file => file.state === 'uploading');
    if (uploadingFiles.length > 0) {
      message.warning('Cannot clear all while files are uploading.');
      return;
    }
    uploadVideoStore.init();
    message.success('All files cleared.');
  };

  // Retry failed files
  const handleRetryFailed = async () => {
    const failedFiles = fileList.filter(file => file.state === 'failed');
    if (failedFiles.length === 0) {
      message.info('No files to retry.');
      return;
    }

    // Change failed files' state to normal
    failedFiles.forEach(file => {
      file.state = 'normal';
      file.percent = 0;
      file.speed = 0;
      uploadVideoStore.updateUploadVideo(file);
    });

    message.info(`${failedFiles.length} files added to retry queue.`);
  };

  // Determine color by upload state
  const getStateColor = (state: string) => {
    switch (state) {
      case 'uploaded':
        return '#52c41a';
      case 'uploading':
        return '#1890ff';
      case 'failed':
        return '#ff4d4f';
      default:
        return '#d9d9d9';
    }
  };

  // Get text by upload state
  const getStateText = (state: string) => {
    switch (state) {
      case 'uploaded':
        return 'Complete';
      case 'uploading':
        return 'Uploading';
      case 'failed':
        return 'Failed';
      default:
        return 'Waiting';
    }
  };

  // Convert speed to readable format
  const formatSpeed = (bytesPerSecond: number): string => {
    if (bytesPerSecond === 0) return '0 B/s';

    const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const k = 1024;
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));

    return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(1)) + ' ' + units[i];
  };

  // Convert file size to readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + units[i];
  };

  const stats = getUploadStats();
  const overallProgress =
    stats.totalSize > 0 ? Math.round((stats.uploadedSize / stats.totalSize) * 100) : 0;

  // Handle upload completion
  useEffect(() => {
    // Process only when upload is finished and files exist
    if (fileList.length > 0 && !uploading) {
      const allCompleted = fileList.every(
        file => file.state === 'uploaded' || file.state === 'failed'
      );

      if (allCompleted) {
        const allSucceeded = fileList.every(file => file.state === 'uploaded');

        if (allSucceeded) {
          // When all files uploaded successfully → move to /assets
          const timer = setTimeout(() => {
            uploadVideoStore.init();
            message.success('All files uploaded successfully. Moving to list page.');
          }, 1000);

          return () => clearTimeout(timer);
        }
        // When there are failed files → stay on current page (do nothing)
      }
    }
  }, [fileList, uploading]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clear list when leaving page
      uploadVideoStore.init();
    };
  }, []);

  return (
    <Component.BasePageComponent>
      <div
        style={{
          padding: '32px',
          backgroundColor: '#f5f7fa',
          minHeight: '100vh',
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '32px',
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <CloudUploadOutlined
            style={{
              fontSize: '48px',
              color: '#1890ff',
              marginBottom: '16px',
            }}
          />
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Video Upload
          </Title>
          <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginTop: '8px' }}>
            Upload video files and monitor progress in real-time
          </Text>
        </div>

        <Row gutter={[24, 24]}>
          {/* Upload area */}
          <Col xs={24} lg={16}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileOutlined style={{ color: '#1890ff' }} />
                  <span>File Upload</span>
                </div>
              }
              extra={
                fileList.length > 0 && (
                  <Space>
                    {stats.failedFiles > 0 && (
                      <Button
                        type="text"
                        icon={<PlayCircleOutlined />}
                        onClick={handleRetryFailed}
                        disabled={uploading}
                      >
                        Retry
                      </Button>
                    )}
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleClearAll}
                      disabled={uploading}
                    >
                      Clear All
                    </Button>
                  </Space>
                )
              }
              style={{
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: 'none',
              }}
            >
              {/* Settings area */}
              <Card
                size="small"
                style={{
                  marginBottom: '24px',
                  backgroundColor: createContent ? '#e6f7ff' : '#f9f9f9',
                  border: createContent ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                }}
              >
                <Row gutter={16} align="middle">
                  <Col flex="auto">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: createContent ? '#1890ff' : '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <CloudUploadOutlined
                          style={{
                            fontSize: '20px',
                            color: createContent ? 'white' : '#999',
                          }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: createContent ? '#1890ff' : '#333',
                          }}
                        >
                          🎬 Create Content Automatically
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            marginTop: '2px',
                          }}
                        >
                          Automatically create content from uploaded video filenames
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <Switch
                      checked={createContent}
                      onChange={setCreateContent}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      disabled={uploading}
                      style={{
                        backgroundColor: createContent ? '#1890ff' : undefined,
                      }}
                    />
                  </Col>
                </Row>

                {createContent && (
                  <div
                    style={{
                      marginTop: '16px',
                      padding: '12px',
                      backgroundColor: 'rgba(24, 144, 255, 0.05)',
                      borderRadius: '6px',
                      borderLeft: '3px solid #1890ff',
                    }}
                  >
                    <div style={{ fontSize: '13px', color: '#1890ff', fontWeight: 'bold' }}>
                      ✨ What will happen:
                    </div>
                    <ul
                      style={{
                        margin: '8px 0 0 0',
                        paddingLeft: '16px',
                        fontSize: '12px',
                        color: '#666',
                      }}
                    >
                      <li>Create content with video filename as title</li>
                      <li>Generate SEO-friendly slug automatically</li>
                      <li>Set uploaded video as default asset</li>
                    </ul>
                  </div>
                )}
              </Card>

              {/* Drag and drop upload */}
              <Dragger
                multiple
                accept="video/*"
                fileList={fileList as any[]}
                onChange={handleFileChange}
                beforeUpload={() => false}
                onRemove={handleRemoveFile}
                disabled={uploading}
                style={{
                  padding: '40px',
                  backgroundColor: uploading ? '#f0f0f0' : '#fafafa',
                  border: '2px dashed #d9d9d9',
                  borderRadius: '12px',
                }}
              >
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined
                    style={{
                      fontSize: '64px',
                      color: uploading ? '#d9d9d9' : '#1890ff',
                    }}
                  />
                </p>
                <p
                  className="ant-upload-text"
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: uploading ? '#999' : 'inherit',
                  }}
                >
                  {uploading ? 'Uploading...' : 'Drag files here or click to select'}
                </p>
                <p className="ant-upload-hint" style={{ fontSize: '14px', color: '#999' }}>
                  Supports video files like MP4, MOV, AVI, MKV. Multiple files can be uploaded
                  simultaneously.
                </p>
              </Dragger>

              {/* Upload button */}
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <Space size="middle">
                  <Button
                    type="primary"
                    size="large"
                    icon={<CloudUploadOutlined />}
                    onClick={handleUpload}
                    loading={uploading}
                    disabled={stats.normalFiles + stats.failedFiles === 0}
                    style={{
                      minWidth: '200px',
                      height: '48px',
                      fontSize: '16px',
                      borderRadius: '8px',
                    }}
                  >
                    {uploading
                      ? 'Uploading...'
                      : createContent
                        ? `🎬 Upload & Create ${stats.normalFiles + stats.failedFiles} Contents`
                        : `Upload ${stats.normalFiles + stats.failedFiles} Files`}
                  </Button>
                  {fileList.length > 0 && !uploading && (
                    <Button
                      size="large"
                      onClick={handleClearAll}
                      style={{
                        height: '48px',
                        borderRadius: '8px',
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                </Space>
              </div>
            </Card>
          </Col>

          {/* Upload status and information */}
          <Col xs={24} lg={8}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Overall upload progress */}
              {stats.totalFiles > 0 && (
                <Card
                  title="Overall Upload Progress"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: 'none',
                  }}
                >
                  <Progress
                    percent={overallProgress}
                    status={
                      stats.failedFiles > 0
                        ? 'exception'
                        : overallProgress === 100
                          ? 'success'
                          : stats.uploadingFiles > 0
                            ? 'active'
                            : 'normal'
                    }
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                    strokeWidth={8}
                  />
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Upload Speed"
                          value={formatSpeed(stats.avgSpeed)}
                          valueStyle={{
                            fontSize: '14px',
                            color: stats.avgSpeed > 0 ? '#1890ff' : '#999',
                          }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Completion Rate"
                          value={`${stats.completedFiles}/${stats.totalFiles}`}
                          valueStyle={{ fontSize: '14px' }}
                        />
                      </Col>
                    </Row>
                  </div>
                  {stats.uploadingFiles > 0 && (
                    <Alert
                      message={`Uploading ${stats.uploadingFiles} files...`}
                      type="info"
                      showIcon
                      style={{ marginTop: '12px' }}
                    />
                  )}
                  {stats.failedFiles > 0 && (
                    <Alert
                      message={`${stats.failedFiles} files failed to upload`}
                      description="Click retry button to upload again"
                      type="error"
                      showIcon
                      style={{ marginTop: '12px' }}
                      action={
                        <Button size="small" danger onClick={handleRetryFailed}>
                          Retry
                        </Button>
                      }
                    />
                  )}
                </Card>
              )}

              {/* Upload statistics */}
              <Card
                title="Upload Status"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: 'none',
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Total Files</Text>
                    <Text strong>{stats.totalFiles}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Completed Files</Text>
                    <Text strong style={{ color: '#52c41a' }}>
                      {stats.completedFiles}
                    </Text>
                  </div>
                  {stats.uploadingFiles > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Uploading</Text>
                      <Text strong style={{ color: '#1890ff' }}>
                        {stats.uploadingFiles}
                      </Text>
                    </div>
                  )}
                  {stats.normalFiles > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Waiting</Text>
                      <Text strong style={{ color: '#d9d9d9' }}>
                        {stats.normalFiles}
                      </Text>
                    </div>
                  )}
                  {stats.failedFiles > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Failed</Text>
                      <Text strong style={{ color: '#ff4d4f' }}>
                        {stats.failedFiles}
                      </Text>
                    </div>
                  )}
                  <Divider style={{ margin: '12px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Total Size</Text>
                    <Text strong>{formatFileSize(stats.totalSize)}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Uploaded</Text>
                    <Text strong>{formatFileSize(stats.uploadedSize)}</Text>
                  </div>
                </Space>
              </Card>

              {/* Settings information */}
              {/* Supported formats */}
              <Card
                title="Supported Formats"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: 'none',
                }}
              >
                <Space wrap>
                  {['MP4', 'MOV', 'AVI', 'MKV', 'WMV', 'FLV'].map(format => (
                    <Tag key={format} color="blue">
                      {format}
                    </Tag>
                  ))}
                </Space>
                <div style={{ marginTop: '12px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Maximum file size: 5GB
                    <br />
                    Recommended resolution: 1080p or higher
                    <br />
                    Recommended bitrate: 8Mbps or higher
                  </Text>
                </div>
              </Card>
            </Space>
          </Col>
        </Row>

        {/* Selected file list */}
        {fileList.length > 0 && (
          <Card
            title={
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>Upload File List ({fileList.length} files)</span>
                <Space>
                  {stats.avgSpeed > 0 && (
                    <Text type="secondary">Average Speed: {formatSpeed(stats.avgSpeed)}</Text>
                  )}
                </Space>
              </div>
            }
            style={{
              marginTop: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none',
            }}
          >
            <Row gutter={[16, 16]}>
              {fileList.map(file => (
                <Col xs={24} sm={12} md={8} lg={6} key={file.uid}>
                  <Card
                    size="small"
                    style={{
                      textAlign: 'center',
                      borderRadius: '8px',
                      border: `2px solid ${getStateColor(file.state)}`,
                      backgroundColor:
                        file.state === 'failed'
                          ? '#fff2f0'
                          : file.state === 'uploaded'
                            ? '#f6ffed'
                            : 'white',
                    }}
                    actions={[
                      <Button
                        key={file.uid}
                        type="text"
                        danger
                        size="small"
                        onClick={() => handleRemoveFile(file)}
                        disabled={file.state === 'uploading'}
                        icon={<DeleteOutlined />}
                      >
                        Remove
                      </Button>,
                    ]}
                  >
                    <div style={{ padding: '12px 0' }}>
                      <div style={{ position: 'relative', marginBottom: '8px' }}>
                        <FileOutlined
                          style={{
                            fontSize: '32px',
                            color: getStateColor(file.state),
                          }}
                        />
                        {/* Status badge */}
                        <Tag
                          color={getStateColor(file.state)}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '20px',
                            fontSize: '10px',
                            lineHeight: '16px',
                            padding: '0 6px',
                          }}
                        >
                          {getStateText(file.state)}
                        </Tag>
                      </div>

                      <div
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '12px',
                        }}
                        title={file.name}
                      >
                        {file.name}
                      </div>

                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '8px' }}>
                        {formatFileSize(file.size || 0)}
                      </div>

                      {/* Individual file progress */}
                      {(file.state === 'uploading' || file.state === 'uploaded') && (
                        <div style={{ marginTop: '8px' }}>
                          <Progress
                            percent={Math.round(file.percent || 0)}
                            size="small"
                            status={file.state === 'uploaded' ? 'success' : 'active'}
                            style={{
                              width: '70%',
                            }}
                            strokeColor={getStateColor(file.state)}
                            showInfo={false}
                          />
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginTop: '4px',
                            }}
                          >
                            <Text style={{ fontSize: '10px', color: '#999' }}>
                              {Math.round(file.percent || 0)}%
                            </Text>
                            {file.state === 'uploading' && file.speed > 0 && (
                              <Text style={{ fontSize: '10px', color: '#999' }}>
                                {formatSpeed(file.speed)}
                              </Text>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Show error for failed files */}
                      {file.state === 'failed' && (
                        <div style={{ marginTop: '8px' }}>
                          <ExclamationCircleOutlined
                            style={{ color: '#ff4d4f', marginRight: '4px' }}
                          />
                          <Text type="danger" style={{ fontSize: '10px' }}>
                            Upload Failed
                          </Text>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        )}
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(AssetAdd);
